import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Plus, Pencil, Trash2, Cake, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/Forms";
import { Panel, Tag } from "@/components/kit";
import { ConfirmDialog, DIALOG_CLS } from "@/components/Dialogs";
import { useStore } from "@/store/AppStore";
import { upcomingBirthdays } from "@/lib/birthdays";
import { fmtDate } from "@/lib/format";
import { TODAY } from "@/data/mock";

const TYPES = { Reunião: "dark", Visita: "gold", Compromisso: "muted", Lembrete: "green" };
const toKey = (d) => format(d, "yyyy-MM-dd");
const toDate = (k) => new Date(`${k}T12:00:00`);

function EventForm({ initial, date, onSave }) {
  const [f, setF] = useState({ title: "", date, time: "09:00", type: "Compromisso", note: "", ...initial });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.date) return toast.error("Informe o título e a data.");
    onSave({ ...f, title: f.title.trim() });
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="event-form">
      <Field label="Título"><Input value={f.title} onChange={set("title")} placeholder="Ex.: Reunião com fornecedor" data-testid="event-title-input" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Data"><Input type="date" value={f.date} onChange={set("date")} data-testid="event-date-input" /></Field>
        <Field label="Horário"><Input type="time" value={f.time} onChange={set("time")} data-testid="event-time-input" /></Field>
      </div>
      <Field label="Tipo">
        <Select value={f.type} onValueChange={set("type")}>
          <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.keys(TYPES).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Observação"><Textarea rows={2} value={f.note} onChange={set("note")} data-testid="event-note-input" /></Field>
      <Button type="submit" className="w-full font-semibold" data-testid="event-save-button">Salvar compromisso</Button>
    </form>
  );
}

function EventItem({ e, onEdit, onDelete, showDate }) {
  return (
    <li data-testid={`event-${e.id}`} className="flex items-start gap-3 py-3">
      <span className="w-12 shrink-0 pt-0.5 text-xs font-semibold text-gold-dark">{e.birthday ? <Cake className="h-4 w-4" /> : e.time}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{e.title}</p>
        <p className="text-xs text-neutral-500">{[showDate && fmtDate(e.date), e.note].filter(Boolean).join(" · ")}</p>
      </div>
      <Tag tone={e.birthday ? "gold" : TYPES[e.type]}>{e.birthday ? "Aniversário" : e.type}</Tag>
      {!e.birthday && (
        <span className="flex">
          <button aria-label="Editar" onClick={onEdit} data-testid={`event-edit-${e.id}`} className="rounded p-1.5 text-neutral-400 hover:text-gold-dark"><Pencil className="h-3.5 w-3.5" /></button>
          <button aria-label="Excluir" onClick={onDelete} data-testid={`event-delete-${e.id}`} className="rounded p-1.5 text-neutral-400 hover:text-gold-dark"><Trash2 className="h-3.5 w-3.5" /></button>
        </span>
      )}
    </li>
  );
}

export function Agenda({ ownerKey, birthdayCompanyIds }) {
  const { state, saveEvent, deleteEvent } = useStore();
  const [day, setDay] = useState(TODAY);
  const [dlg, setDlg] = useState(null);
  const close = (v) => !v && setDlg(null);
  const bdays = upcomingBirthdays(state, birthdayCompanyIds, 365).map((b) => ({ id: `bd-${b.id}`, birthday: true, title: b.name, date: b.date, time: "", note: b.kind === "empresa" ? `${b.years} anos de empresa` : b.company }));
  const all = [...state.events.filter((e) => e.owner === ownerKey), ...bdays].sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));
  const ofDay = all.filter((e) => e.date === day);
  const upcoming = all.filter((e) => e.date >= TODAY && !e.birthday).slice(0, 8);
  const row = (e, showDate) => <EventItem key={e.id} e={e} showDate={showDate} onEdit={() => setDlg({ type: "edit", e })} onDelete={() => setDlg({ type: "del", e })} />;
  const save = (data) => { saveEvent({ ...data, owner: ownerKey }); toast.success(data.id ? "Compromisso atualizado." : "Compromisso agendado."); setDay(data.date); setDlg(null); };
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[auto_1fr]" data-testid="agenda">
      <Panel testId="agenda-calendar" className="lg:w-[340px]">
        <Calendar mode="single" locale={ptBR} today={toDate(TODAY)} defaultMonth={toDate(TODAY)} selected={toDate(day)} onSelect={(d) => d && setDay(toKey(d))} modifiers={{ busy: all.map((e) => toDate(e.date)) }} modifiersClassNames={{ busy: "font-bold text-gold-dark underline decoration-gold decoration-2 underline-offset-4" }} className="mx-auto" />
        <Button className="mt-4 w-full font-semibold" onClick={() => setDlg({ type: "edit" })} data-testid="event-new-button"><Plus className="mr-1 h-4 w-4" /> Novo compromisso</Button>
      </Panel>
      <div className="space-y-6">
        <Panel title={format(toDate(day), "EEEE, d 'de' MMMM", { locale: ptBR })} subtitle={day === TODAY ? "Hoje" : "Dia selecionado"} testId="agenda-day">
          {!ofDay.length && <p className="flex items-center gap-2 text-sm text-neutral-400"><CalendarDays className="h-4 w-4" /> Nenhum compromisso neste dia.</p>}
          <ul className="divide-y divide-neutral-100">{ofDay.map((e) => row(e))}</ul>
        </Panel>
        <Panel title="Próximos compromissos" testId="agenda-upcoming">
          {!upcoming.length && <p className="text-sm text-neutral-400">Nada agendado.</p>}
          <ul className="divide-y divide-neutral-100">{upcoming.map((e) => row(e, true))}</ul>
        </Panel>
      </div>
      <Dialog open={dlg?.type === "edit"} onOpenChange={close}>
        <DialogContent className={DIALOG_CLS} data-testid="event-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{dlg?.e ? "Editar compromisso" : "Novo compromisso"}</DialogTitle></DialogHeader>
          {dlg?.type === "edit" && <EventForm initial={dlg.e} date={day} onSave={save} />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={dlg?.type === "del"} onOpenChange={close} title="Excluir compromisso?" description={dlg?.e?.title} testId="event-delete-dialog" onConfirm={() => { deleteEvent(dlg.e.id); toast.success("Compromisso excluído."); }} />
    </div>
  );
}
